// src/components/SendPanel.jsx
import React, { useContext, useMemo, useState, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import { Users, Eye, Info, Filter, SlidersHorizontal, Shuffle, Eraser, X } from "lucide-react";
import { Survey } from "survey-react-ui";
import { Model } from "survey-core";
import { Loader2 } from "lucide-react";





export default function SendPanel({
  forms,
  emps,
  apiBase,
  authHeaders,
  onSent,
  onError   // <-- add this
}) {
  const [selectedFormId, setSelectedFormId] = useState("");
  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [isSending, setIsSending] = useState(false);


  const [showFormPrev, setShowFormPrev] = useState(false);
  const selectedForm = useMemo(
    () => forms.find((f) => f._id === selectedFormId) || null,
    [forms, selectedFormId]
  );
  const formPrevModel = useMemo(
    () => (selectedForm ? new Model(JSON.parse(JSON.stringify(selectedForm.json))) : null),
    [selectedForm]
  );

  // normalize employees
  const norm = (e) => ({
    _id: e._id,
    name: (e.name ?? e.NAME ?? e["NAME "] ?? "").toString().trim(),
    email: (e.email ?? e["WORK EMAIL"] ?? e["WORK EMAIL "] ?? "").toString().trim(),
    department: (e.department ?? e["DEPARTMENT ESAT"] ?? e["DEPARTMENT ESAT "] ?? "").toString().trim(),
    site: (e.site ?? e.SITE ?? e["Site"] ?? "").toString().trim(),
    position: (e.position ?? e.POSITION ?? e["Position"] ?? "").toString().trim()
  });
  const all = useMemo(() => emps.map(norm), [emps]);

  const allById = useMemo(() => {
  const m = new Map();
  all.forEach((e) => m.set(e._id, e));
  return m;
}, [all]);


  // filters
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [site, setSite] = useState("");
  const [position, setPosition] = useState("");
  const unique = (arr, key) => Array.from(new Set(arr.map((a) => a[key]).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const deptOptions = useMemo(() => unique(all, "department"), [all]);
  const siteOptions = useMemo(() => unique(all, "site"), [all]);
  const posOptions = useMemo(() => unique(all, "position"), [all]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return all.filter((e) => {
      if (dept && e.department !== dept) return false;
      if (site && e.site !== site) return false;
      if (position && e.position !== position) return false;
      if (!qq) return true;
      return [e.name, e.email, e.department, e.site, e.position].some(v => (v || "").toLowerCase().includes(qq));
    });
  }, [all, q, dept, site, position]);

const clearFilters = React.useCallback(() => {
  setQ("");
  setDept("");
  setSite("");
  setPosition("");
  setSelectedEmpIds([]);
}, []);
  // Add this:
  const [viewEmp, setViewEmp] = useState(null); // holds the employee object to view, or null

  // notice banner (success|warn|error)
const [notice, setNotice] = useState(null); // { type, text }


  // confidence sampling
  const [confidence, setConfidence] = useState(95); // %
  const [margin, setMargin] = useState(5);          // % MoE
  const [p, setP] = useState(0.5);                  // expected proportion
  const [stratify, setStratify] = useState(true);

  const zFor = (c) => ({ 80: 1.2816, 85: 1.4395, 90: 1.6449, 95: 1.96, 99: 2.5758 })[c] ?? 1.96;
  const recommendN = useMemo(() => {
    const N = filtered.length; if (N === 0) return 0;
    const z = zFor(confidence), e = Math.max(0.5, Math.min(20, margin)) / 100, pp = Math.min(0.99, Math.max(0.01, p));
    const n0 = (z * z * pp * (1 - pp)) / (e * e); const n = (N * n0) / (N - 1 + n0);
    return Math.min(N, Math.max(1, Math.ceil(n)));
  }, [filtered.length, confidence, margin, p]);

  const randomSample = (arr, k) => {
    const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[a[i], a[j]] = [a[j], a[i]]; }
    return a.slice(0, Math.min(k, a.length));
  };
  const stratifiedBy = (arr, field, k) => {
    const groups = arr.reduce((m, x) => { const key = x[field] || "(unknown)"; (m[key] ||= []).push(x); return m; }, {});
    const keys = Object.keys(groups); const N = arr.length;
    let allocated = 0; const quotas = {};
    keys.forEach(key => { const size = groups[key].length; const q = Math.floor((size / N) * k); quotas[key] = q; allocated += q; });
    let rem = k - allocated;
    const byRem = keys.map(key => [key, groups[key].length / N * k - quotas[key]]).sort((a, b) => b[1] - a[1]);
    for (let i = 0; i < byRem.length && rem > 0; i++, rem--) quotas[byRem[i][0]]++;
    return keys.flatMap(key => randomSample(groups[key], quotas[key]));
  };
  const autoPick = () => {
    const n = recommendN;
    const sample = stratify ? stratifiedBy(filtered, "department", n) : randomSample(filtered, n);
    setSelectedEmpIds(sample.map(s => s._id));
  };

  const isChecked = (id) => selectedEmpIds.includes(id);
  const toggle = (id, checked) => setSelectedEmpIds(prev => checked ? [...new Set([...prev, id])] : prev.filter(x => x !== id));
  const selectAllFiltered = () => setSelectedEmpIds(filtered.map(e => e._id));
  const clearSelection = () => setSelectedEmpIds([]);

const sendSurvey = async () => {


  if (!selectedFormId) return onError?.(new Error("Please select a form."));
  if (selectedEmpIds.length === 0) return onError?.(new Error("Select at least one recipient."));

  const payload = { formId: selectedFormId, recipientIds: selectedEmpIds };

  try {
    setIsSending(true); // 🔄 start spinner

    // 1) Trigger send on backend
    const res = await fetch(`${apiBase}/send`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify(payload),
    });

    // read once (works for both success & error)
    const raw = await res.text();
    let body = {};
    try { body = raw ? JSON.parse(raw) : {}; } catch { /* tolerate non-JSON */ }

    if (!res.ok) {
      const msg = body?.message || raw || `HTTP ${res.status}`;
      throw new Error(`Send failed: ${res.status} – ${msg}`);
    }

    // 2) Emails to notify (skip duplicates / no-email)
    const skipped = Array.isArray(body.skipped) ? body.skipped : [];
    const skippedEmails = new Set(
      skipped.map(s => (s.email || "").toLowerCase()).filter(Boolean)
    );

    const emailsToNotify = selectedEmpIds
      .map(id => (allById.get(id)?.email || "").trim().toLowerCase())
      .filter(e => e && e.includes("@") && !skippedEmails.has(e));

    if (emailsToNotify.length) {
      const { failed = [], error } = await sendSurveyEmails(emailsToNotify);
      if (error) console.warn("Email delivery error", error);
      else if (failed.length) console.warn(`Some emails failed: ${failed.length}`);
    }

    // 3) Cleanup + contextual toast
    clearSelection();

    const inserted = Number(body.inserted || 0);
    const dupCount = skipped.filter(s => s.reason === "duplicate").length;
    const noEmailCount = skipped.filter(s => s.reason === "no email").length;

    const parts = [];
    if (dupCount) parts.push(`${dupCount} duplicate${dupCount > 1 ? "s" : ""}`);
    if (noEmailCount) parts.push(`${noEmailCount} with no email`);
    const tail = parts.length ? ` (${parts.join(", ")} skipped)` : "";

    onSent?.(`Survey sent to ${inserted} recipient${inserted !== 1 ? "s" : ""}${tail}.`);
  } catch (err) {
    console.error("❌ sendSurvey failed:", err);
    onError?.(err);
  } finally {
    setIsSending(false); // ✅ always stop spinner
  }
};



// Ideally set at build time; falls back to your requested fixed URL
const SURVEY_BASE =
  process.env.REACT_APP_SURVEY_URL || "http://10.5.30.50:3006/esatsurvey";

const sendSurveyEmails = async (emails = []) => {
  // Clean + dedupe
  const unique = [...new Set(
    (emails || []).map(String).map(e => e.trim()).filter(e => e.includes("@"))
  )];
  if (unique.length === 0) return { sent: [], failed: [] };

  // Build the final survey URL; keep the form param if you need it
  const surveyUrl =
    `${SURVEY_BASE}${selectedFormId ? `?form=${encodeURIComponent(selectedFormId)}` : ""}`;

  // Build payload
  const payload = {
    recipients: unique,
    subject: "ESAT Survey Invitation",
    surveyUrl, // <-- fixed base instead of window.location.origin
    formTitle: selectedForm?.title ?? "Employee Survey",
  };

  // Ensure we call /api/emails (strip /esat if present)
  const apiRoot = (apiBase || "").replace(/\/esat(?:\/.*)?$/i, "");

  try {
    const res = await fetch(`${apiRoot}/emails/batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Email batch failed: ${res.status} ${text}`);
    }

    const json = await res.json();
    console.log("📨 Email batch result:", json);
    return {
      sent: Array.isArray(json.sent) ? json.sent : [],
      failed: Array.isArray(json.failed) ? json.failed : [],
    };
  } catch (err) {
    console.error("❌ sendSurveyEmails error:", err);
    return { sent: [], failed: unique, error: String(err) };
  }
};


  

  const label = (e) => `${e.name || "(no name)"}${e.department ? " · " + e.department : ""}`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* choose form + sampling */}
      <div className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow">
        {/* Card header */}
        <div className="rounded-t-lg px-4 py-3 bg-black text-white dark:bg-uasYellow dark:text-black border-b">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold">Choose Existing Form</h4>
            <button
              disabled={!selectedForm}
              onClick={() => setShowFormPrev(true)}
              title={selectedForm ? "Preview form" : "Select a form to preview"}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border
                          bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200
                          focus:outline-none focus:ring-2 focus:ring-black
                          disabled:opacity-50
                          dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700 dark:focus:ring-uasYellow"
            >
              <Eye className="w-4 h-4" /> Preview
            </button>
          </div>
        </div>

        <div className="p-6">
          <select
            value={selectedFormId}
            onChange={(e) => setSelectedFormId(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border
                        bg-white text-gray-900 border-gray-300 placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-black
                        dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:placeholder-gray-400 dark:focus:ring-uasYellow"
          >
            <option value="">-- select survey form --</option>
            {forms.map((f) => (
              <option key={f._id} value={f._id}>{f.title}</option>
            ))}
          </select>

          {/* Confidence controls */}
          <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <SlidersHorizontal className="w-4 h-4" />
              <h4 className="text-sm font-semibold">Confidence Sampling</h4>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <label className="text-sm">
                <span className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Confidence</span>
                <select
                  value={confidence}
                  onChange={(e) => setConfidence(parseInt(e.target.value, 10))}
                  className="w-full px-2 py-2 rounded-lg border
                              bg-white text-gray-900 border-gray-300
                              focus:outline-none focus:ring-2 focus:ring-black
                              dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:focus:ring-uasYellow"
                >
                  {[80, 85, 90, 95, 99].map(v => <option key={v} value={v}>{v}%</option>)}
                </select>
              </label>

              <label className="text-sm">
                <span className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Margin of error</span>
                <input
                  type="number" min={0.5} max={20} step={0.5}
                  value={margin}
                  onChange={(e) => setMargin(parseFloat(e.target.value || 5))}
                  className="w-full px-2 py-2 rounded-lg border
                              bg-white text-gray-900 border-gray-300
                              focus:outline-none focus:ring-2 focus:ring-black
                              dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:focus:ring-uasYellow"
                />
              </label>

              <label className="text-sm">
                <span className="block text-xs mb-1 text-gray-700 dark:text-gray-300">p (default 0.5)</span>
                <input
                  type="number" min={0.01} max={0.99} step={0.01}
                  value={p}
                  onChange={(e) => setP(parseFloat(e.target.value || 0.5))}
                  className="w-full px-2 py-2 rounded-lg border
                              bg-white text-gray-900 border-gray-300
                              focus:outline-none focus:ring-2 focus:ring-black
                              dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:focus:ring-uasYellow"
                />
              </label>
            </div>

            <div className="flex items-center justify-between mt-3">
              <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                <input type="checkbox" checked={stratify} onChange={e => setStratify(e.target.checked)} />
                Stratify by Department
              </label>
              <div className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">{filtered.length}</span> match · recommended sample{" "}
                <span className="font-medium">{recommendN}</span>
              </div>
            </div>

            <div className="mt-3 flex gap-2">
              <button
                onClick={autoPick}
                disabled={filtered.length === 0}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold
                            bg-black text-white hover:opacity-90
                            focus:outline-none focus:ring-2 focus:ring-black
                            disabled:opacity-50
                            dark:bg-uasYellow dark:text-black dark:focus:ring-uasYellow"
              >
                <Shuffle className="w-4 h-4" /> Auto-pick sample
              </button>

              <button
                onClick={clearSelection}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm
                            bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200
                            focus:outline-none focus:ring-2 focus:ring-black
                            dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700 dark:focus:ring-uasYellow"
              >
                <Eraser className="w-4 h-4" /> Clear selection
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* recipients + filters */}
      <div className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow">
        {/* Card header */}
        <div className="rounded-t-lg px-4 py-3 bg-black text-white dark:bg-uasYellow dark:text-black border-b">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold">Select Recipients</h4>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 opacity-60" />
              <button
                onClick={clearFilters}
                title="Clear filters"
                className="text-xs px-2 py-1 rounded-lg border
                            bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200
                            focus:outline-none focus:ring-2 focus:ring-black
                            dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700 dark:focus:ring-uasYellow"
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
            <input
              placeholder="Search name, email, team…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="px-3 py-2 rounded-lg border
                          bg-white text-gray-900 border-gray-300 placeholder-gray-400
                          focus:outline-none focus:ring-2 focus:ring-black
                          dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:placeholder-gray-400 dark:focus:ring-uasYellow"
            />
            <select
              value={dept}
              onChange={(e) => setDept(e.target.value)}
              className="px-3 py-2 rounded-lg border
                          bg-white text-gray-900 border-gray-300
                          focus:outline-none focus:ring-2 focus:ring-black
                          dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:focus:ring-uasYellow"
            >
              <option value="">All departments</option>
              {deptOptions.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select
              value={site}
              onChange={(e) => setSite(e.target.value)}
              className="px-3 py-2 rounded-lg border
                          bg-white text-gray-900 border-gray-300
                          focus:outline-none focus:ring-2 focus:ring-black
                          dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:focus:ring-uasYellow"
            >
              <option value="">All sites</option>
              {siteOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              className="px-3 py-2 rounded-lg border
                          bg-white text-gray-900 border-gray-300
                          focus:outline-none focus:ring-2 focus:ring-black
                          dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:focus:ring-uasYellow"
            >
              <option value="">All positions</option>
              {posOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* recipients list */}
          <div className="h-56 overflow-auto border rounded-lg p-2 recipient-list
                            bg-white border-gray-200
                            dark:bg-[#0d1629] dark:border-gray-700">
            {filtered.map((e) => (
              <div
                key={e._id}
                className="flex items-center justify-between gap-2 p-2 rounded
                            hover:bg-gray-50
                            dark:hover:bg-gray-800/50"
              >
                <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100">
                  <input type="checkbox" checked={isChecked(e._id)} onChange={(ev) => toggle(e._id, ev.target.checked)} />
                  <span>{label(e)}</span>
                </label>
                <button
                  type="button"
                  title={e.email}
                  onClick={() => setViewEmp(e)}          // <-- open modal
                  className="p-2 rounded-lg border
              bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200
              focus:outline-none focus:ring-2 focus:ring-black
              dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700 dark:focus:ring-uasYellow"
                >
                  <Info className="w-4 h-4" />
                </button>

              </div>
            ))}
          </div>

          

          <div className="mt-3 flex items-center justify-between text-sm">
            <div className="text-gray-700 dark:text-gray-300">
              Selected <span className="font-semibold">{selectedEmpIds.length}</span> of{" "}
              <span className="font-semibold">{filtered.length}</span> filtered
            </div>
            <div className="flex gap-2">
              <button
                onClick={selectAllFiltered}
                className="px-3 py-2 rounded-lg border text-sm
                            bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200
                            focus:outline-none focus:ring-2 focus:ring-black
                            dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700 dark:focus:ring-uasYellow"
              >
                Select all filtered
              </button>
            </div>
          </div>

      <button
  onClick={sendSurvey}
  disabled={isSending || !selectedFormId || selectedEmpIds.length === 0}
  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold
             bg-black text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-black
             disabled:opacity-50 dark:bg-uasYellow dark:text-black dark:focus:ring-uasYellow"
>
  {isSending ? (
    <>
      <Loader2 className="w-4 h-4 animate-spin" />
      Sending…
    </>
  ) : (
    <>
      <Users className="w-4 h-4" />
      Send Survey
    </>
  )}
</button>

        </div>
      </div>

      {/* Form Preview Modal */}
      {showFormPrev && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-laryzeDarkGray rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold">{selectedForm?.title || "Form preview"}</h3>
              <button onClick={() => setShowFormPrev(false)} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">{formPrevModel ? <Survey model={formPrevModel} /> : <div>No form selected.</div>}</div>
          </div>
        </div>
      )}

      {/* Recipient Info Modal */}
  {/* Recipient Info Modal (theme-aligned) */}
{viewEmp && (
  <div
    className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
    onClick={(ev) => { if (ev.target === ev.currentTarget) setViewEmp(null); }}
  >
    <div className="bg-white dark:bg-laryzeDarkGray rounded-xl shadow-xl w-full max-w-md overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b
                      border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Recipient Details
        </h3>
        <button
          onClick={() => setViewEmp(null)}
          className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700
                     focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-uasYellow"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 space-y-3 text-sm text-gray-800 dark:text-gray-100">
        <div className="font-medium">
          {`${viewEmp.name || "(no name)"}${viewEmp.department ? " · " + viewEmp.department : ""}`}
        </div>

        <div className="grid grid-cols-3 gap-x-3 gap-y-2">
          <div className="text-gray-600 dark:text-gray-300">Email</div>
          <div className="col-span-2">
            {viewEmp.email
              ? <a href={`mailto:${viewEmp.email}`} className="underline text-blue-600 dark:text-blue-400">{viewEmp.email}</a>
              : "—"}
          </div>

          <div className="text-gray-600 dark:text-gray-300">Department</div>
          <div className="col-span-2">{viewEmp.department || "—"}</div>

          <div className="text-gray-600 dark:text-gray-300">Site</div>
          <div className="col-span-2">{viewEmp.site || "—"}</div>

          <div className="text-gray-600 dark:text-gray-300">Position</div>
          <div className="col-span-2">{viewEmp.position || "—"}</div>
        </div>
      </div>

      <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
        <button
          onClick={() => setViewEmp(null)}
          className="px-3 py-2 rounded-lg border
                     bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200
                     focus:outline-none focus:ring-2 focus:ring-black
                     dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700 dark:focus:ring-uasYellow"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}


    </div>




  );
}
